import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { AGENCY_CATEGORIES, CATEGORY_LIST, CATEGORY_LABELS } from "../constants/agencyCategories";
import { buildCreateUserPayload, createUser } from "../services/userService";

const AddUserPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.state?.activeTab || "Customers";
  const [activeTab] = useState(initialTab);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [address, setAddress] = useState("");
  const [agencyCategory, setAgencyCategory] = useState(AGENCY_CATEGORIES.CARS);
  const [saving, setSaving] = useState(false);

  const isVendor = activeTab === "Vendors";
  const role = isVendor ? "Agent" : "User";

  const handleSave = async () => {
    if (!name.trim() || !email.trim() || !password) {
      toast.error("Name, email, and password are required.");
      return;
    }
    if (password !== passwordConfirm) {
      toast.error("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      const payload = buildCreateUserPayload({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
        role,
        addressText: address,
        agencyCategory: isVendor ? agencyCategory : undefined,
      });

      await toast.promise(createUser(payload), {
        loading: "Creating user...",
        success: "User created successfully",
        error: (e) => e?.friendlyMessage || "Failed to create user",
      });

      navigate(`/Users?selected=${isVendor ? "vendors" : "customers"}`);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const formFields = (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="font-bold text-md dark:text-gray-300">Full Name</label>
        <input
          type="text"
          placeholder="Name"
          className="agarly-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="flex gap-4 md:flex-row flex-col">
        <div className="w-full md:w-1/2 flex flex-col space-y-2">
          <label className="font-bold text-md dark:text-gray-300">Email</label>
          <input
            type="email"
            placeholder="email@example.com"
            className="agarly-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="w-full md:w-1/2 flex flex-col space-y-2">
          <label className="font-bold text-md dark:text-gray-300">Phone</label>
          <input
            type="text"
            placeholder="+201000000000"
            className="agarly-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-4 md:flex-row flex-col">
        <div className="w-full md:w-1/2 flex flex-col space-y-2">
          <label className="font-bold text-md dark:text-gray-300">Password</label>
          <input
            type="password"
            className="agarly-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="w-full md:w-1/2 flex flex-col space-y-2">
          <label className="font-bold text-md dark:text-gray-300">Confirm Password</label>
          <input
            type="password"
            className="agarly-input"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
          />
        </div>
      </div>

      {isVendor && (
        <div className="space-y-2">
          <label className="font-bold text-md dark:text-gray-300">Agency Category</label>
          <select
            className="agarly-input"
            value={agencyCategory}
            onChange={(e) => setAgencyCategory(e.target.value)}
          >
            {CATEGORY_LIST.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_LABELS[cat]}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex flex-col space-y-2">
        <label className="font-bold text-md dark:text-gray-300">Address / City</label>
        <textarea
          placeholder="City or full address"
          className="agarly-input min-h-[100px]"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
      </div>

      <button
        type="button"
        disabled={saving}
        onClick={handleSave}
        className="agarly-btn-primary w-full py-2 disabled:opacity-50"
      >
        {saving ? "Saving..." : `Save ${isVendor ? "Vendor" : "Customer"}`}
      </button>
    </div>
  );

  return (
    <div className="md:p-10 p-4 pb-4 text-base gap-4 w-full">
      <h1 className="text-2xl font-bold mb-2 dark:text-gray-200">
        Add {isVendor ? "Vendor" : "Customer"}
      </h1>
      <p className="text-agarly-gray pb-6 text-sm flex flex-wrap gap-1 items-center">
        <Link to="/dashboard" className="hover:underline">Dashboard</Link> /{" "}
        <Link to="/Users" className="hover:underline">Users</Link> /{" "}
        <Link to={`/Users?selected=${isVendor ? "vendors" : "customers"}`} className="hover:underline">
          {activeTab}
        </Link>{" "}
        / <span className="agarly-gradient-text font-bold">Add</span>
      </p>

      <div className="agarly-card p-6 md:w-[60%]">{formFields}</div>
      <div style={{ height: "40px" }} />
    </div>
  );
};

export default AddUserPage;
